import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Icon,
  CircularProgress,
  MenuItem,
  FormControl,
  IconButton,
  Avatar,
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import DataTable from "components/DataTable";
import PageHeader from "components/PageHeader";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import usePaymentData from "layouts/tables/data/paymentTableData";
import PropTypes from "prop-types";
import { API_BASE, API_HOST } from "config/api";

// Helper function to get image URL
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  if (path.startsWith("blob")) return path;
  return `${API_HOST}/storage/${path}`;
};

function Welfare() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayment,
    setSelectedPayment,
    handleEditSubmit,
    handlePaystackSubmit,
    handleDeleteConfirm,
    handleAddPayment,
    members,
    isLoadingMembers,
    searchTerm,
    setSearchTerm,
    filteredMembers,
    showMemberDropdown,
    setShowMemberDropdown,
    handleMemberSelect,
    loadAllMembers,
    searchMembers,
  } = usePaymentData("welfare");

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    if (!selectedPayment?.reference && selectedPayment?.method !== "paystack") {
      setSelectedPayment((prev) => ({
        ...prev,
        reference: `WEL-${Date.now().toString().slice(-6)}`,
      }));
    }
  }, [selectedPayment, setSelectedPayment]);

  // Load members when modal opens
  useEffect(() => {
    if (editModalOpen) {
      loadAllMembers();
    }
  }, [editModalOpen, loadAllMembers]);

  const handlePaystackPayment = useCallback(async () => {
    setIsProcessingPayment(true);
    try {
      await handlePaystackSubmit();
    } finally {
      setIsProcessingPayment(false);
    }
  }, [handlePaystackSubmit]);

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading Welfare Payments...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <PageHeader
                title="Welfare Payments"
                actions={
                  <>
                    <Button variant="contained" onClick={handleAddPayment}>
                      <Icon>add</Icon>&nbsp;New Welfare Payment
                    </Button>
                    <Button variant="contained" onClick={handlePrint}>
                      <Icon>print</Icon>
                    </Button>
                  </>
                }
              />
              <Box pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search welfare payment..."
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Edit/Create Payment Dialog */}
      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedPayment?.id ? "Edit Payment" : "Create New Welfare Payment"}
        onSave={selectedPayment?.method === "manual" ? handleEditSubmit : handlePaystackPayment}
        saveLabel={
          selectedPayment?.method === "manual"
            ? selectedPayment?.id
              ? "Save Changes"
              : "Create Payment"
            : isProcessingPayment
            ? "Processing..."
            : selectedPayment?.id
            ? "Save Changes"
            : "Pay Via Paystack"
        }
        saveDisabled={
          selectedPayment?.method === "manual"
            ? !selectedPayment?.member_id
            : !selectedPayment?.member_id || !selectedPayment?.email
        }
        loading={selectedPayment?.method !== "manual" && isProcessingPayment}
        maxWidth="md"
      >
        <FormSection>
          <FormControl fullWidth>
            <TextField
              label="Search Member"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value.trim() !== "") {
                  setShowMemberDropdown(true);
                } else {
                  setShowMemberDropdown(false);
                }
              }}
              onFocus={() => {
                if (searchTerm.trim() !== "" && filteredMembers.length > 0) {
                  setShowMemberDropdown(true);
                }
              }}
              onBlur={() => setTimeout(() => setShowMemberDropdown(false), 200)}
              fullWidth
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <>
                    {searchTerm && !isLoadingMembers && (
                      <IconButton
                        onClick={() => {
                          setSearchTerm("");
                          setSelectedPayment((prev) => ({
                            ...prev,
                            member_id: "",
                            member_name: "",
                            memberId: "",
                            memberPhoto: "",
                          }));
                        }}
                        size="small"
                      >
                        <Icon>clear</Icon>
                      </IconButton>
                    )}
                    {isLoadingMembers ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      <IconButton
                        onClick={() => {
                          if (searchTerm.trim()) {
                            setShowMemberDropdown(true);
                            searchMembers(searchTerm);
                          }
                        }}
                        size="small"
                      >
                        <Icon>search</Icon>
                      </IconButton>
                    )}
                  </>
                ),
              }}
            />
            {showMemberDropdown && (
              <div
                style={{
                  position: "relative",
                  zIndex: 1300,
                  width: "100%",
                  maxHeight: "250px",
                  overflowY: "auto",
                  backgroundColor: "white",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  marginTop: "4px",
                }}
              >
                {filteredMembers.length > 0 ? (
                  filteredMembers.map((member) => (
                    <MenuItem
                      key={member.id}
                      onClick={() => handleMemberSelect(member)}
                      sx={{
                        padding: "8px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <Avatar
                        src={getImageUrl(member.photo)}
                        sx={{ width: 40, height: 40 }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "";
                        }}
                      >
                        {!member.photo && <Icon>person</Icon>}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {member.first_name} {member.surname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {member.memberID}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled sx={{ padding: "8px 16px", color: "#999" }}>
                    {isLoadingMembers ? "Loading..." : "No members found"}
                  </MenuItem>
                )}
              </div>
            )}
          </FormControl>
          {selectedPayment?.member_id && (
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <Avatar
                src={getImageUrl(selectedPayment.memberPhoto)}
                sx={{ width: 50, height: 50 }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "";
                }}
              >
                {!selectedPayment.memberPhoto && <Icon>person</Icon>}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={700}>
                  {selectedPayment.member_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {selectedPayment.memberId}
                </Typography>
              </Box>
            </Box>
          )}

          {selectedPayment?.method === "paystack" && (
            <TextField
              label="Email"
              type="email"
              value={selectedPayment?.email || ""}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  email: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              required
            />
          )}
        </FormSection>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormSection sx={{ flex: 1, minWidth: 240 }}>
            <TextField
              label="Amount (GHS)"
              type="number"
              value={selectedPayment?.amount || ""}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  amount: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              required
            />

            <TextField
              select
              label="Payment Method"
              value={selectedPayment?.method || "manual"}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  method: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              required
            >
              {["manual", "paystack"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </FormSection>

          <FormSection sx={{ flex: 1, minWidth: 240 }}>
            <TextField
              label="Reference Number"
              value={selectedPayment?.reference || ""}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  reference: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              InputProps={{
                readOnly: selectedPayment?.method !== "paystack" && !!selectedPayment?.reference,
              }}
            />

            <Box sx={{ display: "flex", gap: 1, mt: 1, mb: 0.5 }}>
              <TextField
                select
                label="Month"
                value={selectedPayment?.period_month || new Date().getMonth() + 1}
                onChange={(e) => {
                  const m = Number(e.target.value);
                  const y = selectedPayment?.period_year || new Date().getFullYear();
                  const mm = String(m).padStart(2, "0");
                  setSelectedPayment((prev) => ({
                    ...prev,
                    period_month: m,
                    paymentDate: `${y}-${mm}-01`,
                  }));
                }}
                sx={{ flex: 2 }}
                margin="normal"
                required
              >
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((name, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Year"
                value={selectedPayment?.period_year || new Date().getFullYear()}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  const m = selectedPayment?.period_month || new Date().getMonth() + 1;
                  const mm = String(m).padStart(2, "0");
                  setSelectedPayment((prev) => ({
                    ...prev,
                    period_year: y,
                    paymentDate: `${y}-${mm}-01`,
                  }));
                }}
                sx={{ flex: 1 }}
                margin="normal"
                required
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <TextField
              select
              label="Status"
              value={selectedPayment?.status || "active"}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  status: e.target.value,
                })
              }
              fullWidth
              margin="normal"
            >
              {["active", "inactive"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </FormSection>
        </Box>
      </FormDialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message="Are you sure you want to delete this payment?"
        confirmLabel="Confirm Delete"
      />
    </Box>
  );
}

Welfare.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  handlePrint: PropTypes.func,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedPayment: PropTypes.object,
  setSelectedPayment: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handlePaystackSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddPayment: PropTypes.func,
  members: PropTypes.array,
  isLoadingMembers: PropTypes.bool,
  searchTerm: PropTypes.string,
  setSearchTerm: PropTypes.func,
  filteredMembers: PropTypes.array,
  showMemberDropdown: PropTypes.bool,
  setShowMemberDropdown: PropTypes.func,
  handleMemberSelect: PropTypes.func,
  loadAllMembers: PropTypes.func,
};

export default Welfare;
